import { Browser } from '@capacitor/browser'
import { env } from '@/env'
import { isDesktop, isNative } from './platform'
import { notifyError } from './notify'
import { saveReturnPath } from './return-path'
import { useConnectStore } from '@/store/connect'

/**
 * Discord OAuth, per shell.
 *
 * The Quasar app assigned `window.location.href = "https://discord.com/..."`.
 * That only works in a browser tab. Inside the Capacitor WebView it navigates
 * the app itself away with no way back, and Discord refuses to render its
 * consent screen in an embedded WebView at all; in Electron it replaces the
 * app window with a remote page. So each shell gets the handoff it supports:
 *
 *   web       a new tab when connecting an account (see connectPlatform);
 *             a full-page redirect for signing in and adding the bot
 *   mobile    system browser (SFSafariViewController / Custom Tab), returning
 *             through the app's custom scheme
 *   desktop   the default browser, returning through the hosted redirect page
 */

const AUTHORIZE = 'https://discord.com/oauth2/authorize'

/** Permissions the giveaway bot requires. Checked again on the callback. */
export const GUILD_BOT_PERMISSIONS = '469830673'

function redirectUri(path: '/auth/discord' | '/auth/discord/guild'): string {
  if (isNative()) return `${env.nativeDeepLinkScheme}://auth${path.replace('/auth', '')}`
  return `${env.oauthRedirectOrigin}${path}`
}

function buildUrl(params: Record<string, string>): string {
  const search = new URLSearchParams({ client_id: env.discordClientId, ...params })
  return `${AUTHORIZE}?${search.toString()}`
}

/**
 * OAuth `state` marking a callback that runs in a tab opened just for it. The
 * callback page then says it's done instead of navigating back into the app;
 * the tab that opened it hears about the new connection over the account
 * events socket.
 */
export const CONNECT_TAB_STATE = 'connect-tab'

/** Sign in / register with Discord (or, signed in, link it). */
export function discordLoginUrl(state?: string): string {
  return buildUrl({
    response_type: 'code',
    redirect_uri: redirectUri('/auth/discord'),
    scope: 'identify email guilds guilds.join',
    prompt: 'none',
    ...(state ? { state } : {}),
  })
}

/**
 * Where to send a user to connect a platform the API says they need.
 *
 * Discord takes the shell-aware URL built above: the stored `auth_url` carries
 * the web redirect_uri, which is wrong inside the Capacitor WebView and in
 * Electron. Any other platform uses the link the API supplied, so adding one
 * server-side does not need a client release.
 */
export function linkPlatformUrl(
  platform: { name: string; auth_url?: string | null },
  state?: string,
): string | null {
  return platform.name.toLowerCase() === 'discord'
    ? discordLoginUrl(state)
    : platform.auth_url || null
}

/** What connectPlatform did. */
export type ConnectResult = 'tab' | 'left' | 'unavailable'

/**
 * Connect `platform` to the signed-in account.
 *
 * The callback (e.g. /auth/discord) links the account to whoever is signed in,
 * so this is the same call for "connect" on the profile page, in the giveaway
 * wizard, and in the toast shown when an entry needs a linked account.
 *
 * On the web it opens in a new tab and this page stays as it is -- a half
 * filled-in form included. The new connection reaches this tab over the
 * account events socket (useAccountEvents). Must be called straight from a
 * click, or the browser blocks the tab; then, and in the native and desktop
 * shells, it falls back to leaving the page, calling `beforeLeave` first so
 * the caller can save its work.
 */
export async function connectPlatform(
  platform: { name: string; auth_url?: string | null },
  options: { beforeLeave?: () => void } = {},
): Promise<ConnectResult> {
  if (!linkPlatformUrl(platform)) {
    notifyError(`${platform.name} cannot be connected yet.`)
    return 'unavailable'
  }

  if (!isNative() && !isDesktop()) {
    // Opened blank and pointed at Discord afterwards: with 'noopener' the
    // browser returns null even on success, so a blocked tab couldn't be told
    // apart. Cutting `opener` keeps the other site from reaching this page.
    const tab = window.open('', '_blank')
    if (tab) {
      tab.opener = null
      tab.location.href = linkPlatformUrl(platform, CONNECT_TAB_STATE) as string
      useConnectStore.getState().start(platform.name)
      return 'tab'
    }
  }

  // Desktop finishes in the system browser and stays open, so it waits too.
  if (isDesktop()) useConnectStore.getState().start(platform.name)
  options.beforeLeave?.()
  saveReturnPath(window.location.pathname + window.location.search)
  await startOAuth(linkPlatformUrl(platform) as string)
  return 'left'
}

/** Invite the giveaway bot into a specific guild. */
export function discordAddBotUrl(guildId?: string): string {
  return buildUrl({
    response_type: 'code',
    redirect_uri: redirectUri('/auth/discord/guild'),
    scope: 'bot',
    permissions: GUILD_BOT_PERMISSIONS,
    ...(guildId ? { guild_id: guildId } : {}),
  })
}

/**
 * Send the user to `url` to authorise, in whatever way this shell allows.
 * Callers should persist any in-progress work first: on web and desktop the
 * app is torn down by the navigation.
 */
export async function startOAuth(url: string): Promise<void> {
  if (isNative()) {
    await Browser.open({ url, presentationStyle: 'popover' })
    return
  }
  if (isDesktop()) {
    // Electron's window-open handler routes this to the system browser.
    window.open(url, '_blank', 'noopener,noreferrer')
    return
  }
  window.location.assign(url)
}
